import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HiOutlineTag,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineSparkles,
} from 'react-icons/hi';
import { setDiscountCode } from '../../../../store/basket/basketSlice';
import { apiIsCouponActive } from '../../../../services/OrderService';
import toast from '../../../ui/toast/toast';
import Notification from '../../../ui/Notification';

/**
 * Premium "Apply coupon" modal.
 * - Clear hero with coupon icon, title, subtitle.
 * - Refined input with accent focus ring + inline Apply button.
 * - Applied state with green check + Clear action.
 * - Subtle savings hint under the input.
 */
const CouponModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.basket.basket);
  const store = useSelector((state) => state.base.common.currentStore);
  const [code, setCode] = useState(basket.discountcode || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setCode(basket.discountcode || '');
    setError('');
  }, [basket.discountcode]);

  const applied = !!basket.discountcode;
  const canApply = code.trim().length > 0 && !submitting;

  const handleApply = async (e) => {
    e?.preventDefault?.();
    if (!canApply) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await apiIsCouponActive({
        code: code.trim(),
        storeid: store?.id,
      });
      if (response?.data) {
        dispatch(setDiscountCode(code.trim()));
        toast.push(
          <Notification title="Coupon applied" type="success" duration={2000} />,
          { placement: 'top-center' },
        );
        onClose?.();
      } else {
        setError("That code isn't valid for this restaurant.");
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    dispatch(setDiscountCode(''));
    setCode('');
    setError('');
    toast.push(
      <Notification title="Coupon removed" type="info" duration={2000} />,
      { placement: 'top-center' },
    );
  };

  return (
    <div
      className="w-full max-w-[420px] mx-auto relative"
      style={{
        padding: '28px 26px',
        fontFamily: 'Geist, system-ui, sans-serif',
      }}
    >
      <style>{`
        @keyframes cmFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cm-fade { animation: cmFadeUp 0.42s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .cm-input:focus-within {
          border-color: var(--ui-ink) !important;
          box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>

      {/* Icon hero */}
      <div
        className="cm-fade grid place-items-center mx-auto mb-4"
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '14px',
          background: applied
            ? 'rgba(21, 128, 61, 0.12)'
            : 'var(--accent-soft, var(--site-color-10))',
          color: applied ? 'var(--ui-green)' : 'var(--accent, var(--site-color))',
          border: '1px solid var(--ui-line)',
        }}
      >
        {applied ? (
          <HiOutlineCheck size={24} strokeWidth={2.4} />
        ) : (
          <HiOutlineTag size={22} />
        )}
      </div>

      <h2
        className="cm-fade text-center"
        style={{
          animationDelay: '0.04s',
          fontSize: '22px',
          fontWeight: 800,
          letterSpacing: '-0.025em',
          color: 'var(--ui-ink)',
          lineHeight: 1.15,
          marginBottom: '6px',
        }}
      >
        {applied ? 'Coupon applied' : 'Apply a coupon'}
      </h2>
      <p
        className="cm-fade text-center mb-5"
        style={{
          animationDelay: '0.08s',
          fontSize: '13.5px',
          color: 'var(--ui-ink-2)',
          lineHeight: 1.5,
        }}
      >
        {applied
          ? 'Nice — your savings are already in the basket.'
          : 'Enter your code below. Savings update at checkout.'}
      </p>

      {/* Applied vs. input state */}
      {applied ? (
        <div
          className="cm-fade flex items-center justify-between"
          style={{
            animationDelay: '0.1s',
            padding: '12px 14px',
            borderRadius: '12px',
            background: 'rgba(21, 128, 61, 0.06)',
            border: '1px solid rgba(21, 128, 61, 0.25)',
            marginBottom: '14px',
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <HiOutlineCheck
              size={16}
              style={{ color: 'var(--ui-green)', flexShrink: 0 }}
            />
            <span
              className="truncate"
              style={{
                fontFamily: 'Geist Mono, monospace',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                color: 'var(--ui-ink)',
              }}
            >
              {basket.discountcode}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-[4px] transition-colors"
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              fontWeight: 700,
              borderRadius: '8px',
              background: 'transparent',
              color: 'var(--ui-ink-2)',
              border: '1px solid var(--ui-line-2)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <HiOutlineX size={12} />
            Remove
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleApply}
          className="cm-fade"
          style={{ animationDelay: '0.1s' }}
        >
          <div
            className="cm-input flex items-center overflow-hidden mb-[8px]"
            style={{
              padding: '3px',
              borderRadius: '12px',
              background: 'var(--ui-surface)',
              border: `1px solid ${error ? '#DC2626' : 'var(--ui-line-2)'}`,
              transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
          >
            <HiOutlineTag
              size={15}
              style={{
                color: 'var(--ui-ink-3)',
                marginLeft: '10px',
                flexShrink: 0,
              }}
            />
            <input
              type="text"
              autoComplete="off"
              autoFocus
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                if (error) setError('');
              }}
              placeholder="Enter coupon code"
              className="flex-1 outline-none min-w-0"
              style={{
                padding: '10px 12px',
                background: 'transparent',
                border: 'none',
                fontFamily: 'Geist Mono, monospace',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: 'var(--ui-ink)',
                textTransform: 'uppercase',
              }}
            />
            <button
              type="submit"
              disabled={!canApply}
              className="font-bold transition-transform"
              style={{
                padding: '0 14px',
                height: '36px',
                fontSize: '12.5px',
                borderRadius: '9px',
                background: canApply
                  ? 'var(--accent, var(--site-color))'
                  : 'var(--ui-surface-2)',
                color: canApply
                  ? 'var(--accent-fg, #FFFFFF)'
                  : 'var(--ui-ink-3)',
                border: 'none',
                cursor: canApply ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                margin: '0 2px',
              }}
            >
              {submitting ? 'Checking…' : 'Apply'}
            </button>
          </div>
          {error ? (
            <div
              style={{
                fontSize: '12px',
                color: '#B91C1C',
                fontWeight: 500,
                marginTop: '4px',
              }}
            >
              {error}
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-[4px]"
              style={{
                fontSize: '11.5px',
                color: 'var(--ui-ink-3)',
                fontWeight: 500,
              }}
            >
              <HiOutlineSparkles size={11} />
              Codes are case-insensitive
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default CouponModal;
